import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';

type WorkspaceContext = {
  source?: string;
  resolvedAt?: string;
  customerId?: string;
  customerName?: string;
  tenantId?: string;
  subscriptionId?: string;
  resourceGroup?: string;
  workspaceId?: string;
  workspaceName?: string;
  workspaceResourceId?: string;
  lighthouseMode?: boolean;
  authMode?: string;
};

type AgentStatus = {
  ok: boolean;
  configured: boolean;
  enabled: boolean;
  mode: string;
  baseUrlConfigured: boolean;
  apiPrefix: string;
  functionKeyConfigured: boolean;
  sharedSecretConfigured?: boolean;
  modelHint: string;
  workspaceContextStatus?: string;
  workspaceContextError?: string | null;
  workspaceContext?: WorkspaceContext | null;
  modelCompatibility?: string;
  repoCompatibilityNote?: string;
  externalReachable: boolean;
  serviceReachable?: boolean;
  serviceLabel?: string;
  hostingMode?: string;
  externalHealth?: unknown;
  externalError?: string | null;
};

type AgentRequest = {
  id: string;
  customerId: string;
  customerName?: string | null;
  tenantId?: string | null;
  subscriptionId?: string | null;
  resourceGroup?: string | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  workspaceResourceId?: string | null;
  workspaceContext?: WorkspaceContext | null;
  workspaceContextStatus?: string | null;
  workspaceContextError?: string | null;
  externalRequestId?: string | null;
  externalCampaignId?: string | null;
  message: string;
  status: string;
  route?: string | null;
  intent?: string | null;
  confidence?: number | null;
  response?: string | null;
  rawStatus?: unknown;
  rawResponse?: unknown;
  errorMessage?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
};

type RequestsResponse = { ok: boolean; requests: AgentRequest[] };
type AskResponse = { ok: boolean; request: AgentRequest };

type CampaignSnapshot = {
  id: string;
  customerId: string;
  requestId?: string | null;
  externalCampaignId: string;
  clientName?: string | null;
  status?: string | null;
  currentPhase?: string | null;
  timeRange?: string | null;
  focusAreas?: unknown;
  findingsCount?: number | null;
  hypothesesCount?: number | null;
  totalKqlQueries?: number | null;
  rawCampaign?: unknown;
  rawProgress?: unknown;
  rawReport?: unknown;
  reportMarkdown?: string | null;
  promotedFindingsCount?: number;
  createdByEmail?: string | null;
  createdAt?: string;
  updatedAt?: string;
  lastPolledAt?: string | null;
  completedAt?: string | null;
};

type CampaignsResponse = { ok: boolean; campaigns: CampaignSnapshot[] };
type CampaignStatusResponse = { ok: boolean; campaign: CampaignSnapshot | Record<string, unknown>; rawCampaign?: unknown };
type CampaignProgressResponse = { ok: boolean; progress: Record<string, unknown>; campaign?: CampaignSnapshot | null };
type CampaignReportResponse = { ok: boolean; report?: unknown; markdown?: string | null; campaign?: CampaignSnapshot | null };
type PromoteFindingsResponse = { ok: boolean; promoted: number; findingIds: string[]; message?: string };

type IntelJob = {
  id: string;
  customerId: string;
  jobType: string;
  externalId?: string | null;
  status: string;
  title?: string | null;
  sourceUrl?: string | null;
  inputText?: string | null;
  rawSubmit?: unknown;
  rawResult?: unknown;
  errorMessage?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
};
type IntelJobsResponse = { ok: boolean; jobs: IntelJob[] };
type IntelJobResponse = { ok: boolean; job: IntelJob; raw?: unknown };
type AgentReportsResponse = { ok: boolean; reports: unknown };


type DiagnosticsResponse = {
  ok: boolean;
  diagnostics: {
    generatedAt?: string;
    config?: Record<string, unknown>;
    agentHealth?: Record<string, unknown>;
    workspace?: {
      localStatus?: string;
      localError?: string | null;
      context?: WorkspaceContext | null;
      localChecks?: Record<string, unknown>;
      externalProbe?: Record<string, unknown>;
    };
    recentActivity?: Record<string, unknown>;
  };
};

type AutoHuntTheme = {
  id: string;
  name: string;
  description?: string;
  defaultLookback?: string;
  recommendedCadence?: string;
  focusAreas?: unknown;
  raw?: unknown;
};
type AutoHuntThemesResponse = { ok: boolean; externalOk?: boolean; themes: AutoHuntTheme[]; raw?: unknown };

type AutoHuntRun = {
  id: string;
  customerId: string;
  scheduleId?: string | null;
  themeId: string;
  themeName?: string | null;
  status: string;
  externalRunId?: string | null;
  externalCampaignId?: string | null;
  lookback?: string | null;
  rawSubmit?: unknown;
  rawResult?: unknown;
  errorMessage?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
};
type AutoHuntRunsResponse = { ok: boolean; runs: AutoHuntRun[] };
type AutoHuntRunResponse = { ok: boolean; run: AutoHuntRun };

type AutoHuntSchedule = {
  id: string;
  customerId: string;
  themeId: string;
  themeName?: string | null;
  cadence: string;
  enabled: boolean;
  lookback?: string | null;
  notifyOnFindingsOnly?: boolean;
  saveToCampaignCenter?: boolean;
  recipients?: unknown;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  lastRunId?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
};
type AutoHuntSchedulesResponse = { ok: boolean; schedules: AutoHuntSchedule[] };
type AutoHuntScheduleResponse = { ok: boolean; schedule: AutoHuntSchedule };


type IntelSchedule = {
  id: string;
  customerId: string;
  jobType: string;
  title?: string | null;
  cadence: string;
  enabled: boolean;
  sourceUrl?: string | null;
  inputText?: string | null;
  notifyOnFindingsOnly?: boolean;
  autoPromoteFindings?: boolean;
  recipients?: unknown;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  lastJobId?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
};
type IntelSchedulesResponse = { ok: boolean; schedules: IntelSchedule[] };
type IntelScheduleResponse = { ok: boolean; schedule: IntelSchedule };

type BulkHuntItem = {
  id: string;
  customerId: string;
  customerName?: string | null;
  status: string;
  agentRequestId?: string | null;
  intelJobId?: string | null;
  autoHuntRunId?: string | null;
  externalCampaignId?: string | null;
  errorMessage?: string | null;
  updatedAt?: string;
};
type BulkHuntRun = {
  id: string;
  scope: string;
  huntType: string;
  status: string;
  totalCustomers: number;
  completedCustomers: number;
  failedCustomers: number;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  items: BulkHuntItem[];
};
type BulkRunsResponse = { ok: boolean; runs: BulkHuntRun[] };
type BulkRunResponse = { ok: boolean; run: BulkHuntRun };

type Props = {
  customerId: string;
  canRun: boolean;
  canViewCampaigns?: boolean;
  canExportCampaigns?: boolean;
  canPromoteFindings?: boolean;
  canDeleteHistory?: boolean;
  canViewIntel?: boolean;
  canRunIntel?: boolean;
  canExportIntel?: boolean;
  canViewDiagnostics?: boolean;
  canRunDiagnostics?: boolean;
  canViewAutoHunt?: boolean;
  canRunAutoHunt?: boolean;
  canManageAutoHunt?: boolean;
  canViewScheduledIntel?: boolean;
  canRunScheduledIntel?: boolean;
  canManageScheduledIntel?: boolean;
  canViewBulkHunts?: boolean;
  canRunBulkHunts?: boolean;
};

const QUICK_PROMPTS = [
  'Hunt for identity compromise indicators in the last 24 hours. Focus on risky sign-ins, MFA fatigue, consent grants and privilege changes.',
  'Check whether this customer shows ransomware precursor activity: suspicious PowerShell, lateral movement, mass file operations and backup tampering.',
  'Are there signs of C2 or suspicious outbound activity from firewall, proxy, DNS or endpoint telemetry?',
  'Generate a hunt campaign for credential theft, lateral movement and persistence across all available Sentinel data sources.',
  'Assess exposure and hunt evidence for a critical CVE mentioned in recent threat intelligence.',
];

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
}

function statusBadge(status: string): string {
  const s = status.toLowerCase();
  if (s === 'completed') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  if (s === 'processing' || s === 'submitted' || s === 'running') return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30';
  if (s === 'error' || s === 'failed') return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
  if (s === 'not_configured') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
  return 'bg-muted text-muted-foreground border-border';
}

function prettyStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function maskGuid(value?: string | null): string {
  if (!value) return '—';
  const clean = String(value);
  return clean.length > 12 ? `${clean.slice(0, 8)}…${clean.slice(-4)}` : clean;
}

function contextBadge(status?: string | null): string {
  const s = String(status || '').toLowerCase();
  if (s === 'resolved') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  if (s === 'missing_workspace') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
  if (s === 'access_denied' || s === 'customer_not_found') return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
  return 'bg-muted text-muted-foreground border-border';
}

function listFromUnknown(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => typeof v === 'string' ? v : JSON.stringify(v)).filter(Boolean).slice(0, 12);
  if (typeof value === 'string' && value.trim()) return value.split(/[,;]/).map((v) => v.trim()).filter(Boolean).slice(0, 12);
  return [];
}

function objectFromUnknown(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function progressEvents(progress?: Record<string, unknown> | null): Array<Record<string, unknown>> {
  if (!progress) return [];
  const direct = progress.events;
  if (Array.isArray(direct)) return direct.map(objectFromUnknown).filter((x) => Object.keys(x).length).slice(-40);
  const data = objectFromUnknown(progress.data);
  if (Array.isArray(data.events)) return data.events.map(objectFromUnknown).filter((x) => Object.keys(x).length).slice(-40);
  return [];
}


function boolWord(value: unknown): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value == null || value === '') return 'Unknown';
  return String(value);
}

function boolWordWithStatus(value: unknown, supported?: unknown): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (supported === false) return 'Unsupported';
  if (supported === true && (value == null || value === '')) return 'Not reported';
  return boolWord(value);
}

function jsonPreview(value: unknown): string {
  if (value == null || value === '') return '';
  try {
    return JSON.stringify(value, null, 2).slice(0, 6000);
  } catch {
    return String(value).slice(0, 6000);
  }
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  }
  return '';
}

function healthBadge(value: unknown): string {
  if (value === true || String(value).toLowerCase() === 'reachable' || String(value).toLowerCase() === 'healthy') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  if (value === false || String(value).toLowerCase() === 'failed' || String(value).toLowerCase() === 'error') return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
  return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
}

function healthLabel(value: unknown, note?: unknown, configured?: unknown): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (configured === true) return 'Configured';
  if (note) return 'Not reported';
  return boolWord(value);
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ThreatHuntAgentPanel({ customerId, canRun, canViewCampaigns = true, canExportCampaigns = false, canPromoteFindings = false, canDeleteHistory = false, canViewIntel = false, canRunIntel = false, canExportIntel = false, canViewDiagnostics = false, canRunDiagnostics = false, canViewAutoHunt = false, canRunAutoHunt = false, canManageAutoHunt = false, canViewScheduledIntel = false, canRunScheduledIntel = false, canManageScheduledIntel = false, canViewBulkHunts = false, canRunBulkHunts = false }: Props) {
  const queryClient = useQueryClient();
  const { customers } = useCustomers();
  const [message, setMessage] = useState(QUICK_PROMPTS[0]);
  const [selected, setSelected] = useState<AgentRequest | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [intelText, setIntelText] = useState('');
  const [intelUrl, setIntelUrl] = useState('');
  const [intelTitle, setIntelTitle] = useState('');
  const [selectedIntelJob, setSelectedIntelJob] = useState<IntelJob | null>(null);
  const [selectedAutoThemeId, setSelectedAutoThemeId] = useState('identity_compromise');
  const [autoLookback, setAutoLookback] = useState('24h');
  const [autoCadence, setAutoCadence] = useState('daily');
  const [intelScheduleJobType, setIntelScheduleJobType] = useState<'kev_scan' | 'intel_scan' | 'targeted_intel_hunt'>('kev_scan');
  const [intelScheduleCadence, setIntelScheduleCadence] = useState('daily');
  const [bulkHuntType, setBulkHuntType] = useState<'agent_prompt' | 'auto_hunt' | 'kev_scan' | 'intel_scan' | 'targeted_intel_hunt'>('agent_prompt');
  const [bulkCustomerIds, setBulkCustomerIds] = useState<string[]>([customerId]);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [lastWorkspaceAccessDiagnostics, setLastWorkspaceAccessDiagnostics] = useState<DiagnosticsResponse | null>(null);

  const clearFeedback = () => { setError(null); setNotice(null); };
  const showNotice = (value: string) => { setError(null); setNotice(value); };
  const showError = (value: string) => { setNotice(null); setError(value); };

  const statusQuery = useQuery({
    queryKey: ['huntops-agent-status', customerId],
    queryFn: async () => apiFetch<AgentStatus>(`/hunts/agent/status?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId,
  });

  const requestsQuery = useQuery({
    queryKey: ['huntops-agent-requests', customerId],
    queryFn: async () => apiFetch<RequestsResponse>(`/hunts/agent/requests?customerId=${encodeURIComponent(customerId)}&limit=75`),
    enabled: !!customerId,
  });

  const campaignsQuery = useQuery({
    queryKey: ['huntops-agent-campaigns', customerId],
    queryFn: async () => apiFetch<CampaignsResponse>(`/hunts/agent/campaigns?customerId=${encodeURIComponent(customerId)}&limit=50`),
    enabled: !!customerId && canViewCampaigns,
  });


  const intelJobsQuery = useQuery({
    queryKey: ['huntops-agent-intel-jobs', customerId],
    queryFn: async () => apiFetch<IntelJobsResponse>(`/hunts/agent/intel-jobs?customerId=${encodeURIComponent(customerId)}&limit=50`),
    enabled: !!customerId && canViewIntel,
  });

  const intelReportsQuery = useQuery({
    queryKey: ['huntops-agent-intel-reports', customerId],
    queryFn: async () => apiFetch<AgentReportsResponse>(`/hunts/agent/intel-reports?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && canViewIntel,
    retry: false,
  });

  const kevReportsQuery = useQuery({
    queryKey: ['huntops-agent-kev-reports', customerId],
    queryFn: async () => apiFetch<AgentReportsResponse>(`/hunts/agent/kev-reports?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && canViewIntel,
    retry: false,
  });


  const diagnosticsQuery = useQuery({
    queryKey: ['huntops-agent-diagnostics', customerId],
    queryFn: async () => apiFetch<DiagnosticsResponse>(`/hunts/agent/diagnostics?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && canViewDiagnostics,
    retry: false,
  });

  const autoThemesQuery = useQuery({
    queryKey: ['huntops-agent-auto-themes', customerId],
    queryFn: async () => apiFetch<AutoHuntThemesResponse>(`/hunts/agent/auto-hunt/themes?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && canViewAutoHunt,
    retry: false,
  });

  const autoRunsQuery = useQuery({
    queryKey: ['huntops-agent-auto-runs', customerId],
    queryFn: async () => apiFetch<AutoHuntRunsResponse>(`/hunts/agent/auto-hunt/runs?customerId=${encodeURIComponent(customerId)}&limit=50`),
    enabled: !!customerId && canViewAutoHunt,
  });

  const autoSchedulesQuery = useQuery({
    queryKey: ['huntops-agent-auto-schedules', customerId],
    queryFn: async () => apiFetch<AutoHuntSchedulesResponse>(`/hunts/agent/auto-hunt/schedules?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && canViewAutoHunt,
  });


  const intelSchedulesQuery = useQuery({
    queryKey: ['huntops-agent-intel-schedules', customerId],
    queryFn: async () => apiFetch<IntelSchedulesResponse>(`/hunts/agent/intel-schedules?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && canViewScheduledIntel,
  });

  const bulkRunsQuery = useQuery({
    queryKey: ['huntops-agent-bulk-runs', customerId],
    queryFn: async () => apiFetch<BulkRunsResponse>(`/hunts/agent/bulk-runs?customerId=${encodeURIComponent(customerId)}&limit=25`),
    enabled: !!customerId && canViewBulkHunts,
  });

  const status = statusQuery.data;
  const requests = requestsQuery.data?.requests ?? [];
  const latest = selected ?? requests[0] ?? null;
  const campaigns = campaignsQuery.data?.campaigns ?? [];
  const intelJobs = intelJobsQuery.data?.jobs ?? [];
  const latestIntelJob = selectedIntelJob ?? intelJobs[0] ?? null;
  const baseDiagnostics = diagnosticsQuery.data?.diagnostics ?? null;
  const diagnostics = lastWorkspaceAccessDiagnostics?.diagnostics ?? baseDiagnostics;
  const agentHealth = objectFromUnknown(diagnostics?.agentHealth);
  const diagWorkspace = objectFromUnknown(diagnostics?.workspace);
  const externalProbe = objectFromUnknown(diagWorkspace.externalProbe);
  const externalProbeRaw = externalProbe.raw;
  const externalProbeRawObj = objectFromUnknown(externalProbeRaw);
  const externalProbeSucceeded = externalProbe.functionCanQueryWorkspace === true
    && externalProbe.sentinelReaderPermission !== false
    && externalProbe.logAnalyticsReaderPermission !== false;
  const externalProbeError = firstText(
    externalProbe.error,
    externalProbeRawObj.error,
    externalProbeRawObj.errorMessage,
    externalProbeSucceeded ? undefined : externalProbeRawObj.detail,
    externalProbeSucceeded ? undefined : externalProbeRawObj.details,
    agentHealth.externalError,
  );
  const externalProbeMessage = firstText(externalProbe.message, externalProbeRawObj.message);
  const externalProbeContractHint = firstText(externalProbe.contractHint);
  const externalProbeJson = jsonPreview(externalProbeRaw || externalProbe);
  const externalProbeOpenApi = objectFromUnknown(externalProbe.openApi || agentHealth.openApi);
  const externalProbePaths = listFromUnknown(externalProbe.availablePaths || externalProbeOpenApi.paths);
  const fallbackProbe = objectFromUnknown(externalProbe.fallbackProbe);
  const workspaceProbeMode = externalProbe.dedicatedProbeSupported === true
    ? 'Dedicated probe'
    : fallbackProbe.attempted === true
      ? 'Fallback via Ask/Chat'
      : externalProbe.attempted === true
        ? 'Endpoint missing'
        : 'Not run';
  const workspaceProbeEndpointStatus = String(externalProbe.endpointStatus || '').replace(/_/g, ' ');
  const fallbackStatusNumber = Number(fallbackProbe.status ?? externalProbe.fallbackHttpStatus ?? 0);
  const fallbackOk = fallbackProbe.ok === true || (Number.isFinite(fallbackStatusNumber) && fallbackStatusNumber >= 200 && fallbackStatusNumber < 300);
  const dedicatedProbeMissing = externalProbe.dedicatedProbeSupported === false && String(externalProbe.endpointStatus || '') === 'dedicated_probe_missing';
  const workspaceAgentReachableLabel = dedicatedProbeMissing ? (fallbackOk ? 'Reachable via Ask/Chat' : 'Reachability not verified') : boolWordWithStatus(externalProbe.functionCanQueryWorkspace, externalProbe.supported);
  const workspaceStructuredProbeLabel = dedicatedProbeMissing ? 'Missing from Hunt Agent service' : (externalProbe.dedicatedProbeSupported === true ? 'Available' : boolWord(externalProbe.dedicatedProbeSupported));
  const workspaceSentinelReaderLabel = dedicatedProbeMissing ? 'Requires dedicated probe' : boolWordWithStatus(externalProbe.sentinelReaderPermission, externalProbe.supported);
  const workspaceLaReaderLabel = dedicatedProbeMissing ? 'Requires dedicated probe' : boolWordWithStatus(externalProbe.logAnalyticsReaderPermission, externalProbe.supported);
  const workspaceProbeTitle = dedicatedProbeMissing ? 'Agent reachable / RBAC probe missing' : 'Workspace probe';
  const isLocalAgentMode = status?.mode === 'local';
  const serviceReachable = status?.serviceReachable ?? status?.externalReachable ?? agentHealth.serviceReachable ?? agentHealth.functionReachable;
  const serviceModeLabel = isLocalAgentMode ? 'Local VM sidecar' : status?.mode === 'external' ? 'External Hunt Agent' : prettyStatus(status?.mode || 'unknown');
  const adapterModeRaw = String(agentHealth.sentinelAdapterMode || status?.hostingMode || '').trim();
  const adapterModeLabel = isLocalAgentMode || adapterModeRaw === 'vm-sidecar'
    ? 'Local VM sidecar'
    : (adapterModeRaw || 'Unknown');
  const recentActivity = objectFromUnknown(diagnostics?.recentActivity);
  const autoThemes = autoThemesQuery.data?.themes ?? [];
  const autoRuns = autoRunsQuery.data?.runs ?? [];
  const autoSchedules = autoSchedulesQuery.data?.schedules ?? [];
  const intelSchedules = intelSchedulesQuery.data?.schedules ?? [];
  const bulkRuns = bulkRunsQuery.data?.runs ?? [];
  const bulkEligibleCustomers = customers.filter((c) => c.enabled !== false && c.id !== 'default' && Boolean(c.workspaceId));
  const selectedAutoTheme = autoThemes.find((t) => t.id === selectedAutoThemeId) ?? autoThemes[0] ?? null;
  const activeCampaignId = selectedCampaignId || latest?.externalCampaignId || autoRuns.find((r) => r.externalCampaignId)?.externalCampaignId || campaigns[0]?.externalCampaignId || null;

  const campaignStatusQuery = useQuery({
    queryKey: ['huntops-agent-campaign-status', customerId, activeCampaignId],
    queryFn: async () => apiFetch<CampaignStatusResponse>(`/hunts/agent/campaigns/${encodeURIComponent(activeCampaignId || '')}?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && !!activeCampaignId && canViewCampaigns,
  });

  const campaignProgressQuery = useQuery({
    queryKey: ['huntops-agent-campaign-progress', customerId, activeCampaignId],
    queryFn: async () => apiFetch<CampaignProgressResponse>(`/hunts/agent/campaigns/${encodeURIComponent(activeCampaignId || '')}/progress?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && !!activeCampaignId && canViewCampaigns,
    refetchInterval: (query) => {
      const statusText = String((query.state.data?.campaign as CampaignSnapshot | null)?.status || '').toLowerCase();
      return ['running', 'starting', 'processing'].includes(statusText) ? 15000 : false;
    },
  });

  const campaignReportQuery = useQuery({
    queryKey: ['huntops-agent-campaign-report', customerId, activeCampaignId],
    queryFn: async () => apiFetch<CampaignReportResponse>(`/hunts/agent/campaigns/${encodeURIComponent(activeCampaignId || '')}/report?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId && !!activeCampaignId && canViewCampaigns,
    retry: false,
  });

  const campaignStatus = campaignStatusQuery.data?.campaign as CampaignSnapshot | undefined;
  const campaignProgress = campaignProgressQuery.data?.progress ?? objectFromUnknown(campaignStatus?.rawProgress);
  const campaignReport = campaignReportQuery.data;
  const campaignMarkdown = campaignReport?.markdown || campaignStatus?.reportMarkdown || '';
  const campaignEvents = progressEvents(campaignProgress);

  const inFlightCount = useMemo(() => requests.filter((r) => ['processing', 'submitted', 'running'].includes(r.status)).length, [requests]);

  const askMutation = useMutation({
    mutationFn: async () => apiFetch<AskResponse>('/hunts/agent/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, message }),
    }),
    onSuccess: (data) => {
      setSelected(data.request);
      if (data.request.externalCampaignId) setSelectedCampaignId(data.request.externalCampaignId);
      clearFeedback();
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-requests', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-status', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Threat Hunt Agent request failed'),
  });

  const refreshMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<AskResponse>(`/hunts/agent/requests/${encodeURIComponent(id)}/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
    }),
    onSuccess: (data) => {
      setSelected(data.request);
      if (data.request.externalCampaignId) setSelectedCampaignId(data.request.externalCampaignId);
      clearFeedback();
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-requests', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-campaigns', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Refresh failed'),
  });

  const promoteMutation = useMutation({
    mutationFn: async (campaignId: string) => apiFetch<PromoteFindingsResponse>(`/hunts/agent/campaigns/${encodeURIComponent(campaignId)}/promote-findings`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
    }),
    onSuccess: (data) => {
      showNotice(data.message || `${data.promoted} finding(s) sent to Findings Inbox.`);
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-campaigns', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Promote findings failed'),
  });


  const deleteRequestMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<{ ok: boolean }>(`/hunts/agent/requests/${encodeURIComponent(id)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      setSelected(null);
      showNotice('Agent request history item soft-deleted.');
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-requests', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Delete request failed'),
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => apiFetch<{ ok: boolean }>(`/hunts/agent/campaigns/${encodeURIComponent(campaignId)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      setSelectedCampaignId(null);
      showNotice('Campaign history item soft-deleted.');
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-campaigns', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Delete campaign failed'),
  });

  const submitIntelMutation = useMutation({
    mutationFn: async (kind: 'intel_scan' | 'kev_scan' | 'targeted_intel_hunt') => {
      const path = kind === 'kev_scan' ? '/hunts/agent/kev-scan' : kind === 'targeted_intel_hunt' ? '/hunts/agent/intel-hunt' : '/hunts/agent/intel-scan';
      return apiFetch<IntelJobResponse>(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, url: intelUrl, text: intelText, title: intelTitle }),
      });
    },
    onSuccess: (data) => {
      setSelectedIntelJob(data.job);
      showNotice('Intel / KEV hunt job submitted. Poll the job for results.');
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-jobs', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Intel / KEV hunt submission failed'),
  });

  const refreshIntelMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<IntelJobResponse>(`/hunts/agent/intel-jobs/${encodeURIComponent(id)}/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
    }),
    onSuccess: (data) => {
      setSelectedIntelJob(data.job);
      clearFeedback();
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-jobs', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-reports', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-kev-reports', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Intel / KEV poll failed'),
  });

  const deleteIntelMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<{ ok: boolean }>(`/hunts/agent/intel-jobs/${encodeURIComponent(id)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      setSelectedIntelJob(null);
      showNotice('Intel / KEV history item soft-deleted.');
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-jobs', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Delete Intel / KEV job failed'),
  });



  const createIntelScheduleMutation = useMutation({
    mutationFn: async () => apiFetch<IntelScheduleResponse>('/hunts/agent/intel-schedules', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, jobType: intelScheduleJobType, title: intelTitle, cadence: intelScheduleCadence, url: intelUrl, text: intelText, recipients: emailRecipients.split(/[,;]/).map((x) => x.trim()).filter(Boolean), notifyOnFindingsOnly: true }),
    }),
    onSuccess: () => { showNotice('Intel / KEV monitoring schedule created.'); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-schedules', customerId] }); },
    onError: (e) => showError(e instanceof Error ? e.message : 'Create Intel / KEV schedule failed'),
  });

  const runIntelScheduleMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<IntelJobResponse>(`/hunts/agent/intel-schedules/${encodeURIComponent(id)}/run`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }) }),
    onSuccess: (data) => { setSelectedIntelJob(data.job); showNotice('Scheduled Intel / KEV monitoring run submitted.'); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-schedules', customerId] }); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-jobs', customerId] }); },
    onError: (e) => showError(e instanceof Error ? e.message : 'Run Intel / KEV schedule failed'),
  });

  const toggleIntelScheduleMutation = useMutation({
    mutationFn: async (schedule: IntelSchedule) => apiFetch<IntelScheduleResponse>(`/hunts/agent/intel-schedules/${encodeURIComponent(schedule.id)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, cadence: schedule.cadence, enabled: !schedule.enabled, notifyOnFindingsOnly: schedule.notifyOnFindingsOnly !== false, autoPromoteFindings: schedule.autoPromoteFindings === true }) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-schedules', customerId] }),
    onError: (e) => showError(e instanceof Error ? e.message : 'Update Intel / KEV schedule failed'),
  });

  const deleteIntelScheduleMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<{ ok: boolean }>(`/hunts/agent/intel-schedules/${encodeURIComponent(id)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' }),
    onSuccess: () => { showNotice('Intel / KEV monitoring schedule soft-deleted.'); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-schedules', customerId] }); },
    onError: (e) => showError(e instanceof Error ? e.message : 'Delete Intel / KEV schedule failed'),
  });

  const runDueIntelSchedulesMutation = useMutation({
    mutationFn: async () => apiFetch<{ ok: boolean; count: number; jobs: IntelJob[] }>('/hunts/agent/intel-schedules/run-due', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, limit: 10 }) }),
    onSuccess: (data) => { showNotice(`${data.count} due Intel / KEV monitoring schedule(s) submitted.`); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-schedules', customerId] }); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-jobs', customerId] }); },
    onError: (e) => showError(e instanceof Error ? e.message : 'Run due Intel / KEV schedules failed'),
  });

  const runBulkHuntMutation = useMutation({
    mutationFn: async () => apiFetch<BulkRunResponse>('/hunts/agent/bulk-run', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope: 'selected_customers',
        customerIds: bulkCustomerIds,
        huntType: bulkHuntType,
        message,
        themeId: selectedAutoTheme?.id || selectedAutoThemeId,
        themeName: selectedAutoTheme?.name || selectedAutoThemeId,
        lookback: autoLookback,
        title: intelTitle,
        url: intelUrl,
        text: intelText,
        recipients: emailRecipients.split(/[,;]/).map((x) => x.trim()).filter(Boolean),
        maxCustomers: 25,
      }),
    }),
    onSuccess: (data) => { showNotice(`Bulk hunt submitted for ${data.run.totalCustomers} customer(s); ${data.run.failedCustomers} failed.`); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-bulk-runs', customerId] }); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-requests', customerId] }); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-intel-jobs', customerId] }); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-runs', customerId] }); },
    onError: (e) => showError(e instanceof Error ? e.message : 'Bulk hunt execution failed'),
  });

  const deleteBulkRunMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<{ ok: boolean }>(`/hunts/agent/bulk-runs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => { showNotice('Bulk hunt run soft-deleted.'); void queryClient.invalidateQueries({ queryKey: ['huntops-agent-bulk-runs', customerId] }); },
    onError: (e) => showError(e instanceof Error ? e.message : 'Delete bulk hunt run failed'),
  });

  const workspaceCheckMutation = useMutation({
    mutationFn: async () => apiFetch<DiagnosticsResponse>('/hunts/agent/diagnostics/workspace-access', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
    }),
    onSuccess: (data) => {
      setLastWorkspaceAccessDiagnostics(data);
      const workspace = objectFromUnknown(data.diagnostics?.workspace);
      const probe = objectFromUnknown(workspace.externalProbe);
      const probeRaw = objectFromUnknown(probe.raw);
      const probeFailed = probe.functionCanQueryWorkspace === false || probe.sentinelReaderPermission === false || probe.logAnalyticsReaderPermission === false;
      const failureMessage = firstText(probe.error, probeRaw.error, probeRaw.errorMessage, probeRaw.detail);
      const successMessage = firstText(probe.message, probeRaw.message) || 'Workspace access diagnostic completed. Review the diagnostics panel.';
      if (probeFailed || failureMessage) showError(failureMessage || successMessage);
      else showNotice(successMessage);
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-diagnostics', customerId] });
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Workspace access diagnostic failed';
      showError(message);
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: async () => apiFetch<{ ok: boolean; result: unknown }>('/hunts/agent/diagnostics/test-email', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, recipients: emailRecipients.split(/[,;]/).map((x) => x.trim()).filter(Boolean) }),
    }),
    onSuccess: () => showNotice('Test email diagnostic submitted to the Hunt Agent service.'),
    onError: (e) => showError(e instanceof Error ? e.message : 'Test email diagnostic failed'),
  });

  const runAutoHuntMutation = useMutation({
    mutationFn: async () => apiFetch<AutoHuntRunResponse>('/hunts/agent/auto-hunt/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, themeId: selectedAutoTheme?.id || selectedAutoThemeId, themeName: selectedAutoTheme?.name || selectedAutoThemeId, lookback: autoLookback }),
    }),
    onSuccess: (data) => {
      showNotice(`Auto-Hunt submitted: ${data.run.themeName || data.run.themeId}`);
      if (data.run.externalCampaignId) setSelectedCampaignId(data.run.externalCampaignId);
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-runs', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-campaigns', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Auto-Hunt run failed'),
  });

  const createAutoScheduleMutation = useMutation({
    mutationFn: async () => apiFetch<AutoHuntScheduleResponse>('/hunts/agent/auto-hunt/schedules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, themeId: selectedAutoTheme?.id || selectedAutoThemeId, themeName: selectedAutoTheme?.name || selectedAutoThemeId, lookback: autoLookback, cadence: autoCadence, enabled: true, notifyOnFindingsOnly: true, saveToCampaignCenter: true }),
    }),
    onSuccess: () => {
      showNotice('Auto-Hunt schedule created.');
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-schedules', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-diagnostics', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Create Auto-Hunt schedule failed'),
  });

  const toggleAutoScheduleMutation = useMutation({
    mutationFn: async (schedule: AutoHuntSchedule) => apiFetch<AutoHuntScheduleResponse>(`/hunts/agent/auto-hunt/schedules/${encodeURIComponent(schedule.id)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, cadence: schedule.cadence, lookback: schedule.lookback || '24h', enabled: !schedule.enabled, notifyOnFindingsOnly: schedule.notifyOnFindingsOnly !== false, saveToCampaignCenter: schedule.saveToCampaignCenter !== false }),
    }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-schedules', customerId] }),
    onError: (e) => showError(e instanceof Error ? e.message : 'Update Auto-Hunt schedule failed'),
  });

  const deleteAutoScheduleMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<{ ok: boolean }>(`/hunts/agent/auto-hunt/schedules/${encodeURIComponent(id)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      showNotice('Auto-Hunt schedule soft-deleted.');
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-schedules', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Delete Auto-Hunt schedule failed'),
  });

  const runAutoScheduleMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<AutoHuntRunResponse>(`/hunts/agent/auto-hunt/schedules/${encodeURIComponent(id)}/run`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
    }),
    onSuccess: (data) => {
      showNotice('Scheduled Auto-Hunt run submitted.');
      if (data.run.externalCampaignId) setSelectedCampaignId(data.run.externalCampaignId);
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-runs', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-schedules', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-campaigns', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Run schedule failed'),
  });

  const refreshAutoRunMutation = useMutation({
    mutationFn: async (id: string) => apiFetch<AutoHuntRunResponse>(`/hunts/agent/auto-hunt/runs/${encodeURIComponent(id)}/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId }),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-auto-runs', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['huntops-agent-campaigns', customerId] });
    },
    onError: (e) => showError(e instanceof Error ? e.message : 'Poll Auto-Hunt run failed'),
  });


  function toggleBulkCustomer(id: string) {
    setBulkCustomerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function selectAllBulkCustomers() {
    setBulkCustomerIds(bulkEligibleCustomers.map((c) => c.id).slice(0, 25));
  }

  async function exportCampaign(format: 'json' | 'markdown') {
    if (!activeCampaignId) return;
    const resp = await fetch(`${getApiBase()}/hunts/agent/campaigns/${encodeURIComponent(activeCampaignId)}/export?customerId=${encodeURIComponent(customerId)}&format=${format}`, { credentials: 'include' });
    const text = await resp.text();
    if (!resp.ok) { showError(text || `Campaign export failed with HTTP ${resp.status}`); return; }
    downloadText(`hunt-agent-${activeCampaignId}.${format === 'markdown' ? 'md' : 'json'}`, text, format === 'markdown' ? 'text/markdown;charset=utf-8' : 'application/json;charset=utf-8');
  }


  async function exportIntelJob(jobId: string, format: 'json' | 'markdown') {
    const resp = await fetch(`${getApiBase()}/hunts/agent/intel-jobs/${encodeURIComponent(jobId)}/export?customerId=${encodeURIComponent(customerId)}&format=${format}`, { credentials: 'include' });
    const text = await resp.text();
    if (!resp.ok) { showError(text || `Intel / KEV export failed with HTTP ${resp.status}`); return; }
    downloadText(`hunt-agent-intel-${jobId}.${format === 'markdown' ? 'md' : 'json'}`, text, format === 'markdown' ? 'text/markdown;charset=utf-8' : 'application/json;charset=utf-8');
  }

  function exportLatest() {
    if (!latest) return;
    downloadText(`huntops-agent-${latest.id}.json`, JSON.stringify(latest, null, 2), 'application/json;charset=utf-8');
  }

  const visibleError = error
    ?? (statusQuery.error instanceof Error ? statusQuery.error.message : null)
    ?? (requestsQuery.error instanceof Error ? requestsQuery.error.message : null)
    ?? (diagnosticsQuery.error instanceof Error ? diagnosticsQuery.error.message : null)
    ?? (autoThemesQuery.error instanceof Error ? autoThemesQuery.error.message : null)
    ?? (intelSchedulesQuery.error instanceof Error ? intelSchedulesQuery.error.message : null)
    ?? (bulkRunsQuery.error instanceof Error ? bulkRunsQuery.error.message : null);
  const workspaceContext = status?.workspaceContext ?? null;
  const workspaceReady = status?.workspaceContextStatus === 'resolved';
  const externalMode = status?.mode === 'external';
  const submitDisabled = !canRun || askMutation.isPending || message.trim().length < 5 || (externalMode && !workspaceReady);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary/80 font-bold">Autonomous Hunt Agent</div>
            <h3 className="mt-1 text-lg font-black text-foreground">MSSP Threat Hunt Agent</h3>
            <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
              Ask natural-language hunt questions, submit async investigations through the configured MSSP Hunt Agent service, poll campaign status, and keep results inside HuntOps audit/history. Local mode uses the in-VM sidecar while preserving the same bounded gpt-4.1-mini request contract.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[10px] uppercase text-muted-foreground">Mode</div><div className="mt-1 font-bold text-foreground">{status?.mode ?? 'loading'}</div></div>
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[10px] uppercase text-muted-foreground">Service</div><div className={`mt-1 font-bold ${serviceReachable ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'}`}>{serviceReachable ? 'Reachable' : status?.configured ? 'Check failed' : 'Not configured'}</div><div className="mt-1 text-[10px] text-muted-foreground">{serviceModeLabel}</div></div>
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[10px] uppercase text-muted-foreground">Model</div><div className="mt-1 font-bold text-foreground">{status?.modelHint ?? 'gpt-4.1-mini'}</div></div>
            <div className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[10px] uppercase text-muted-foreground">In flight</div><div className="mt-1 font-bold text-foreground">{inFlightCount}</div></div>
          </div>
        </div>
        {status?.repoCompatibilityNote && <div className="mt-3 rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-xs text-foreground">{status.repoCompatibilityNote}</div>}
        {status?.externalError && <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">Hunt Agent note: {status.externalError}</div>}
        {notice && <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300 whitespace-pre-wrap">{notice}</div>}
        {visibleError && <div className="mt-3 rounded-lg bg-red-500/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap">{visibleError}</div>}
      </div>

      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary/80 font-bold">Customer Workspace Context</div>
            <h4 className="mt-1 text-sm font-black text-foreground">Dynamic workspace selected for this hunt</h4>
            <p className="mt-1 max-w-3xl text-xs text-muted-foreground">KloudSecure resolves the selected customer from Customer Master and passes only workspace metadata to the Hunt Agent service. In local mode this is the in-VM sidecar; secrets remain in Key Vault / service settings and are not sent in the request body.</p>
          </div>
          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${contextBadge(status?.workspaceContextStatus)}`}>{prettyStatus(status?.workspaceContextStatus || 'loading')}</span>
        </div>
        <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Customer</div><div className="mt-1 font-bold text-foreground">{workspaceContext?.customerName || customerId || '—'}</div></div>
          <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Workspace</div><div className="mt-1 font-bold text-foreground">{workspaceContext?.workspaceName || '—'}</div><div className="mt-1 font-mono text-[10px] text-muted-foreground">{maskGuid(workspaceContext?.workspaceId)}</div></div>
          <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Scope</div><div className="mt-1 font-bold text-foreground">{workspaceContext?.resourceGroup || '—'}</div><div className="mt-1 font-mono text-[10px] text-muted-foreground">{maskGuid(workspaceContext?.subscriptionId)}</div></div>
          <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Access mode</div><div className="mt-1 font-bold text-foreground">{workspaceContext?.authMode ? prettyStatus(workspaceContext.authMode) : '—'}</div><div className="mt-1 text-[10px] text-muted-foreground">Lighthouse: {workspaceContext?.lighthouseMode ? 'Yes' : 'No'}</div></div>
        </div>
        {status?.workspaceContextError && <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">{status.workspaceContextError}</div>}
        {workspaceContext?.workspaceResourceId && <details className="mt-3 rounded-lg border border-border bg-muted/10 p-3"><summary className="cursor-pointer text-xs font-semibold text-muted-foreground">Workspace resource ID</summary><div className="mt-2 break-all font-mono text-[11px] text-muted-foreground">{workspaceContext.workspaceResourceId}</div></details>}
      </div>


      {canViewDiagnostics && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-500">Agent Health & Diagnostics</div>
              <h4 className="mt-1 text-sm font-black text-foreground">Hunt Agent service, LLM, state store and workspace access</h4>
              <p className="mt-1 max-w-4xl text-xs text-muted-foreground">This panel makes the Hunt Agent service supportable across customers: service health, Blob configuration/state, Key Vault resolution, OpenAI deployment, recent asks and an explicit workspace access probe.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void diagnosticsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh Diagnostics</button>
              {canRunDiagnostics && <button onClick={() => workspaceCheckMutation.mutate()} disabled={workspaceCheckMutation.isPending || !workspaceReady} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Workspace Access Check</button>}
            </div>
          </div>
          <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4">
            <div className={`rounded-lg border p-3 ${healthBadge(serviceReachable)}`}><div className="text-[10px] uppercase opacity-80">Agent service reachable</div><div className="mt-1 font-bold">{boolWord(serviceReachable)}</div><div className="mt-1 text-[10px] opacity-80">{serviceModeLabel}</div></div>
            <div className={`rounded-lg border p-3 ${healthBadge(agentHealth.llmAvailable)}`}><div className="text-[10px] uppercase opacity-80">LLM available</div><div className="mt-1 font-bold">{healthLabel(agentHealth.llmAvailable, agentHealth.llmStatusText, agentHealth.llmConfigured)}</div>{agentHealth.llmStatusText != null && agentHealth.llmStatusText !== '' ? <div className="mt-1 text-[10px] opacity-80">{String(agentHealth.llmStatusText)}</div> : null}</div>
            <div className={`rounded-lg border p-3 ${healthBadge(agentHealth.blobStateEnabled)}`}><div className="text-[10px] uppercase opacity-80">Blob state</div><div className="mt-1 font-bold">{healthLabel(agentHealth.blobStateEnabled, agentHealth.blobStateStatusText, agentHealth.blobConfigured)}</div>{agentHealth.blobStateStatusText != null && agentHealth.blobStateStatusText !== '' ? <div className="mt-1 text-[10px] opacity-80">{String(agentHealth.blobStateStatusText)}</div> : null}</div>
            <div className={`rounded-lg border p-3 ${healthBadge(agentHealth.keyVaultReferencesResolved)}`}><div className="text-[10px] uppercase opacity-80">Key Vault refs</div><div className="mt-1 font-bold">{healthLabel(agentHealth.keyVaultReferencesResolved, agentHealth.keyVaultStatusText)}</div>{agentHealth.keyVaultStatusText != null && agentHealth.keyVaultStatusText !== '' ? <div className="mt-1 text-[10px] opacity-80">{String(agentHealth.keyVaultStatusText)}</div> : null}</div>
          </div>
          <div className="mt-3 grid gap-3 text-xs lg:grid-cols-3">
            <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Adapter / model</div><div className="mt-1 font-bold text-foreground">{adapterModeLabel}</div><div className="mt-1 text-muted-foreground">Deployment: {String(agentHealth.openAiDeployment || status?.modelHint || '—')}</div></div>
            <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Recent activity</div><div className="mt-1 text-foreground">In-flight: <span className="font-bold">{String(recentActivity.inFlightRequests ?? inFlightCount)}</span></div><div className="mt-1 text-muted-foreground">Enabled Auto-Hunt schedules: {String(recentActivity.enabledAutoHuntSchedules ?? autoSchedules.length)}</div></div>
            <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">{workspaceProbeTitle}</div><div className="mt-1 font-bold text-foreground">{dedicatedProbeMissing ? 'Agent service' : 'Agent can query'}: {workspaceAgentReachableLabel}</div><div className="mt-1 text-muted-foreground">Structured RBAC probe: {workspaceStructuredProbeLabel}</div><div className="mt-1 text-muted-foreground">Sentinel Reader: {workspaceSentinelReaderLabel} · LA Reader: {workspaceLaReaderLabel}</div><div className="mt-1 text-[10px] text-muted-foreground">Mode: {workspaceProbeMode}{workspaceProbeEndpointStatus ? ` · ${workspaceProbeEndpointStatus}` : ''}</div>{externalProbe.httpStatus != null ? <div className="mt-1 text-[10px] text-muted-foreground">HTTP status: {String(externalProbe.httpStatus)}</div> : null}{externalProbe.fallbackHttpStatus != null ? <div className="mt-1 text-[10px] text-muted-foreground">Fallback HTTP: {String(externalProbe.fallbackHttpStatus)}{externalProbe.fallbackEndpoint ? ` · ${String(externalProbe.fallbackEndpoint)}` : ''}</div> : null}</div>
          </div>
          {externalProbeMessage && <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${externalProbeSucceeded ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>{externalProbeMessage}</div>}
          {externalProbeContractHint ? <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"><span className="font-semibold">Probe contract note:</span> {externalProbeContractHint}</div> : null}
          {String(externalProbe.recommendation ?? '').trim() ? <div className="mt-2 rounded-lg border border-primary/25 bg-primary/10 px-3 py-2 text-xs text-foreground"><span className="font-semibold">Recommended fix:</span> {String(externalProbe.recommendation ?? '').trim()}</div> : null}
          {externalProbeOpenApi.ok === true && (
            <div className="mt-2 rounded-lg border border-border bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Hunt Agent OpenAPI:</span> {String(externalProbeOpenApi.title || 'MSSP Threat Hunt Agent')} {externalProbeOpenApi.version ? `v${String(externalProbeOpenApi.version)}` : ''}
              {externalProbePaths.length ? <span> · exposed routes: {externalProbePaths.slice(0, 8).join(', ')}{externalProbePaths.length > 8 ? '…' : ''}</span> : null}
            </div>
          )}
          {externalProbeError && <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300"><span className="font-semibold">Workspace access detail:</span> {externalProbeError}</div>}
          {(externalProbeJson || workspaceCheckMutation.error) && (
            <details className="mt-3 rounded-lg border border-border bg-muted/10 p-3 text-xs">
              <summary className="cursor-pointer font-semibold text-muted-foreground">Workspace probe raw details</summary>
              {workspaceCheckMutation.error ? <div className="mt-2 rounded border border-red-500/30 bg-red-500/10 p-2 text-red-700 dark:text-red-300">{workspaceCheckMutation.error instanceof Error ? workspaceCheckMutation.error.message : String(workspaceCheckMutation.error)}</div> : null}
              {externalProbeJson ? <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-background p-3 font-mono text-[11px] text-muted-foreground">{externalProbeJson}</pre> : null}
            </details>
          )}
          {canRunDiagnostics && (
            <div className="mt-3 flex flex-col gap-2 rounded-lg border border-border bg-muted/10 p-3 text-xs md:flex-row md:items-center">
              <div className="font-semibold text-foreground">Email delivery diagnostic</div>
              <input value={emailRecipients} onChange={(e) => setEmailRecipients(e.target.value)} placeholder="optional recipient1@domain.com, recipient2@domain.com" className="min-w-0 flex-1 rounded border border-border bg-background px-3 py-2 text-xs" />
              <button onClick={() => testEmailMutation.mutate()} disabled={testEmailMutation.isPending || !status?.configured} className="rounded border border-border px-3 py-2 text-xs hover:bg-muted disabled:opacity-50">Test Email</button>
            </div>
          )}
        </div>
      )}

      {canViewAutoHunt && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500">Auto-Hunt Scheduler + Themes</div>
              <h4 className="mt-1 text-sm font-black text-foreground">Autonomous hunt rotation for customer workspaces</h4>
              <p className="mt-1 max-w-4xl text-xs text-muted-foreground">Load themes from the Hunt Agent service, run one immediately, or register hourly/daily/weekly schedules. Runs are tracked locally and campaign IDs are automatically linked into Campaign Center when returned by the agent.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void autoThemesQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh Themes</button>
              <button onClick={() => void autoRunsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh Runs</button>
            </div>
          </div>
          <div className="mt-4 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/10 p-3">
                <div className="grid gap-2 md:grid-cols-3">
                  <label className="block text-xs font-semibold text-muted-foreground md:col-span-3">Theme</label>
                  <select value={selectedAutoTheme?.id || selectedAutoThemeId} onChange={(e) => setSelectedAutoThemeId(e.target.value)} className="rounded border border-border bg-background px-3 py-2 text-xs md:col-span-3">
                    {autoThemes.map((theme) => <option key={theme.id} value={theme.id}>{theme.name || theme.id}</option>)}
                  </select>
                  <select value={autoLookback} onChange={(e) => setAutoLookback(e.target.value)} className="rounded border border-border bg-background px-3 py-2 text-xs">
                    {['1h','4h','8h','12h','24h','72h','7d','14d','30d'].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                  <select value={autoCadence} onChange={(e) => setAutoCadence(e.target.value)} className="rounded border border-border bg-background px-3 py-2 text-xs">
                    {['hourly','daily','weekly'].map((x) => <option key={x} value={x}>{prettyStatus(x)}</option>)}
                  </select>
                  <button onClick={() => runAutoHuntMutation.mutate()} disabled={!canRunAutoHunt || runAutoHuntMutation.isPending || !workspaceReady || !selectedAutoTheme} className="rounded bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">Run Now</button>
                </div>
                {selectedAutoTheme && <div className="mt-3 rounded border border-border bg-background p-3 text-xs"><div className="font-bold text-foreground">{selectedAutoTheme.name}</div><div className="mt-1 text-muted-foreground">{selectedAutoTheme.description || 'No description returned.'}</div><div className="mt-2 flex flex-wrap gap-1.5">{listFromUnknown(selectedAutoTheme.focusAreas).map((x) => <span key={x} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{x}</span>)}</div></div>}
                {canManageAutoHunt && <button onClick={() => createAutoScheduleMutation.mutate()} disabled={createAutoScheduleMutation.isPending || !workspaceReady || !selectedAutoTheme} className="mt-3 w-full rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50">Create {prettyStatus(autoCadence)} Schedule</button>}
                {!canRunAutoHunt && <div className="mt-2 text-[11px] text-muted-foreground">You have view access only. Request hunts.run_auto_hunt_now to launch themes.</div>}
              </div>
              <div className="rounded-lg border border-border bg-muted/10 p-3">
                <div className="mb-2 text-xs font-bold text-foreground">Schedules</div>
                <div className="max-h-64 overflow-auto space-y-2">
                  {autoSchedules.length ? autoSchedules.map((schedule) => (
                    <div key={schedule.id} className="rounded border border-border bg-background p-2 text-xs">
                      <div className="flex items-start justify-between gap-2"><div><div className="font-bold text-foreground">{schedule.themeName || schedule.themeId}</div><div className="text-muted-foreground">{prettyStatus(schedule.cadence)} · lookback {schedule.lookback || '24h'} · next {formatDate(schedule.nextRunAt)}</div></div><span className={`rounded-full border px-2 py-0.5 text-[10px] ${schedule.enabled ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' : 'bg-muted text-muted-foreground border-border'}`}>{schedule.enabled ? 'Enabled' : 'Disabled'}</span></div>
                      <div className="mt-2 flex flex-wrap gap-1.5">{canRunAutoHunt && <button onClick={() => runAutoScheduleMutation.mutate(schedule.id)} disabled={runAutoScheduleMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Run</button>}{canManageAutoHunt && <button onClick={() => toggleAutoScheduleMutation.mutate(schedule)} disabled={toggleAutoScheduleMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">{schedule.enabled ? 'Disable' : 'Enable'}</button>}{canManageAutoHunt && <button onClick={() => deleteAutoScheduleMutation.mutate(schedule.id)} disabled={deleteAutoScheduleMutation.isPending} className="rounded border border-red-500/30 px-2 py-1 text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}</div>
                    </div>
                  )) : <div className="rounded border border-dashed border-border p-5 text-center text-xs text-muted-foreground">No Auto-Hunt schedules yet.</div>}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="flex items-center justify-between gap-2"><h5 className="text-xs font-bold text-foreground">Recent Auto-Hunt Runs</h5><span className="text-[10px] text-muted-foreground">{autoRuns.length} runs</span></div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Updated</th><th className="px-2 py-2">Theme</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">External</th><th className="px-2 py-2">Action</th></tr></thead>
                  <tbody>{autoRuns.map((run) => (<tr key={run.id} className="border-t border-border align-top"><td className="px-2 py-2 text-muted-foreground">{formatDate(run.updatedAt)}</td><td className="px-2 py-2"><div className="font-semibold text-foreground">{run.themeName || run.themeId}</div><div className="text-[10px] text-muted-foreground">{run.lookback || '24h'}</div></td><td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(run.status)}`}>{prettyStatus(run.status)}</span></td><td className="px-2 py-2 text-muted-foreground"><div className="font-mono text-[10px]">{run.externalRunId || '—'}</div>{run.externalCampaignId && <div className="font-mono text-[10px] text-violet-500">{run.externalCampaignId}</div>}</td><td className="px-2 py-2"><div className="flex flex-wrap gap-1.5"><button onClick={() => refreshAutoRunMutation.mutate(run.id)} disabled={refreshAutoRunMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Poll</button>{run.externalCampaignId && <button onClick={() => setSelectedCampaignId(run.externalCampaignId || null)} className="rounded border border-violet-500/30 px-2 py-1 text-violet-600 hover:bg-violet-500/10">Campaign</button>}</div></td></tr>))}</tbody>
                </table>
                {autoRuns.length === 0 && <div className="rounded border border-dashed border-border p-8 text-center text-xs text-muted-foreground">No Auto-Hunt runs yet. Run a theme or create a schedule.</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {canViewBulkHunts && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-orange-500">Multi-Customer Bulk Hunt Execution</div>
              <h4 className="mt-1 text-sm font-black text-foreground">Run one hunt across selected customer workspaces</h4>
              <p className="mt-1 max-w-4xl text-xs text-muted-foreground">Launch the same natural-language hunt, Auto-Hunt theme, Intel Scan, CISA KEV scan or targeted report hunt across multiple accessible customers. The backend caps each run to 25 customers and stores per-customer results.</p>
            </div>
            <button onClick={() => void bulkRunsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh Bulk Runs</button>
          </div>
          <div className="mt-4 grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-lg border border-border bg-muted/10 p-3 text-xs">
              <div className="flex items-center justify-between gap-2"><h5 className="font-bold text-foreground">Target customers</h5><div className="flex gap-2"><button onClick={() => setBulkCustomerIds([customerId])} className="rounded border border-border px-2 py-1 hover:bg-muted">Current</button><button onClick={selectAllBulkCustomers} className="rounded border border-border px-2 py-1 hover:bg-muted">Select all</button></div></div>
              <div className="mt-3 grid max-h-52 gap-2 overflow-auto sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                {bulkEligibleCustomers.map((c) => (<label key={c.id} className="flex items-center gap-2 rounded border border-border bg-background px-2 py-1.5"><input type="checkbox" checked={bulkCustomerIds.includes(c.id)} onChange={() => toggleBulkCustomer(c.id)} /><span className="truncate text-foreground">{c.name}</span><span className="ml-auto font-mono text-[10px] text-muted-foreground">{maskGuid(c.workspaceId)}</span></label>))}
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <select value={bulkHuntType} onChange={(e) => setBulkHuntType(e.target.value as typeof bulkHuntType)} className="rounded border border-border bg-background px-3 py-2 text-xs">
                  <option value="agent_prompt">Threat Hunt Agent prompt</option>
                  <option value="auto_hunt">Auto-Hunt theme</option>
                  <option value="kev_scan">CISA KEV scan</option>
                  <option value="intel_scan">Intel scan</option>
                  <option value="targeted_intel_hunt">Targeted intel hunt</option>
                </select>
                <button onClick={() => runBulkHuntMutation.mutate()} disabled={!canRunBulkHunts || runBulkHuntMutation.isPending || bulkCustomerIds.length === 0 || (bulkHuntType === 'agent_prompt' && message.trim().length < 5) || (bulkHuntType === 'targeted_intel_hunt' && !intelUrl.trim() && !intelText.trim())} className="rounded bg-orange-600 px-3 py-2 text-xs font-bold text-white hover:bg-orange-700 disabled:opacity-50">{runBulkHuntMutation.isPending ? 'Submitting…' : `Run for ${bulkCustomerIds.length} customer(s)`}</button>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">Uses the current Ask prompt, selected Auto-Hunt theme/lookback, and Intel / KEV URL/text fields depending on the selected bulk type.</div>
            </div>
            <div className="rounded-lg border border-border bg-background p-3 text-xs">
              <div className="flex items-center justify-between gap-2"><h5 className="font-bold text-foreground">Recent Bulk Runs</h5><span className="text-[10px] text-muted-foreground">{bulkRuns.length} runs</span></div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Updated</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Customers</th><th className="px-2 py-2">Action</th></tr></thead>
                  <tbody>{bulkRuns.map((run) => (<tr key={run.id} className="border-t border-border align-top"><td className="px-2 py-2 text-muted-foreground">{formatDate(run.updatedAt)}</td><td className="px-2 py-2 text-foreground">{prettyStatus(run.huntType)}</td><td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(run.status)}`}>{prettyStatus(run.status)}</span></td><td className="px-2 py-2 text-muted-foreground">{run.completedCustomers}/{run.totalCustomers} submitted · {run.failedCustomers} failed</td><td className="px-2 py-2"><div className="flex flex-wrap gap-1.5"><details className="rounded border border-border px-2 py-1"><summary className="cursor-pointer">Items</summary><div className="mt-2 min-w-[320px] space-y-1">{run.items.map((item) => <div key={item.id} className="rounded border border-border bg-muted/10 p-2"><div className="font-semibold text-foreground">{item.customerName || item.customerId} · {prettyStatus(item.status)}</div><div className="font-mono text-[10px] text-muted-foreground">{item.agentRequestId || item.intelJobId || item.autoHuntRunId || item.externalCampaignId || '—'}</div>{item.errorMessage && <div className="mt-1 text-[10px] text-red-600 dark:text-red-300">{item.errorMessage}</div>}</div>)}</div></details>{canRunBulkHunts && <button onClick={() => deleteBulkRunMutation.mutate(run.id)} disabled={deleteBulkRunMutation.isPending} className="rounded border border-red-500/30 px-2 py-1 text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}</div></td></tr>))}</tbody>
                </table>
                {bulkRuns.length === 0 && <div className="rounded border border-dashed border-border p-6 text-center text-xs text-muted-foreground">No multi-customer bulk hunt runs yet.</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[420px_1fr]">
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <h4 className="text-sm font-bold text-foreground">Ask the Hunt Agent</h4>
          <p className="mt-1 text-xs text-muted-foreground">Use plain English. The Hunt Agent service decides whether this is a quick chat answer or a longer campaign.</p>
          <div className="mt-4 space-y-3">
            <label className="block text-xs font-semibold text-muted-foreground">Quick prompts</label>
            <div className="grid gap-2">
              {QUICK_PROMPTS.map((prompt) => (
                <button key={prompt} onClick={() => setMessage(prompt)} className="rounded-lg border border-border bg-muted/10 px-3 py-2 text-left text-[11px] hover:bg-muted">
                  {prompt}
                </button>
              ))}
            </div>
            <label className="block text-xs font-semibold text-muted-foreground">Hunt question / campaign objective</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} className="input-default w-full text-xs" placeholder="Example: Hunt for credential theft and lateral movement across the last 24 hours. Use SigninLogs, AuditLogs, DeviceProcessEvents and SecurityAlert where available." />
            <button onClick={() => askMutation.mutate()} disabled={submitDisabled} className="w-full rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-primary/90 disabled:opacity-50">
              {askMutation.isPending ? 'Submitting…' : 'Submit to Hunt Agent'}
            </button>
            {!canRun && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300">You can view agent history, but your role does not have the HuntOps run_threat_hunt_agent capability.</div>}
            {externalMode && !workspaceReady && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300">External mode requires a resolved customer workspace. Update Customer Master with workspace ID/name/scope before submitting.</div>}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="text-sm font-bold text-foreground">Latest Agent Result</h4>
              <p className="mt-1 text-xs text-muted-foreground">Responses are stored in PostgreSQL and audited through Log Master.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void requestsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh List</button>
              {latest && <button onClick={() => refreshMutation.mutate(latest.id)} disabled={refreshMutation.isPending} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">{refreshMutation.isPending ? 'Polling…' : 'Poll Result'}</button>}
              {latest && <button onClick={exportLatest} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Export JSON</button>}
            </div>
          </div>

          {!latest ? (
            <div className="mt-4 flex min-h-[280px] items-center justify-center rounded-lg border border-dashed border-border text-center text-xs text-muted-foreground">No agent requests yet. Submit a hunt question to start.</div>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(latest.status)}`}>{prettyStatus(latest.status)}</span>
                {latest.route && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{latest.route}</span>}
                {latest.intent && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{latest.intent}</span>}
                {typeof latest.confidence === 'number' && <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">confidence {Math.round(latest.confidence * 100)}%</span>}
                {latest.workspaceName && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">Workspace: {latest.workspaceName}</span>}
                {latest.externalRequestId && <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-muted-foreground">{latest.externalRequestId}</span>}
                {latest.externalCampaignId && <span className="rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 font-mono text-[10px] text-violet-700 dark:text-violet-300">{latest.externalCampaignId}</span>}
              </div>
              <div className="rounded-lg border border-border bg-muted/10 p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Question</div>
                <div className="mt-1 whitespace-pre-wrap text-xs text-foreground">{latest.message}</div>
              </div>
              <div className="rounded-lg border border-border bg-background p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Response</div>
                <div className="mt-2 whitespace-pre-wrap text-sm text-foreground">{latest.response || latest.errorMessage || 'Still processing. Poll again shortly.'}</div>
              </div>
              <details className="rounded-lg border border-border bg-muted/10 p-3">
                <summary className="cursor-pointer text-xs font-semibold text-muted-foreground">Raw response / trace</summary>
                <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded border border-border bg-background p-3 font-mono text-[11px]">{JSON.stringify(latest.rawResponse ?? latest.rawStatus ?? latest, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      </div>

      {canViewCampaigns && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-violet-500 font-bold">Campaign Center</div>
              <h4 className="mt-1 text-sm font-black text-foreground">Threat Hunt Agent Campaigns</h4>
              <p className="mt-1 max-w-3xl text-xs text-muted-foreground">Long-running autonomous hunts are tracked here. Poll campaign status, view live progress, render the final report, export artifacts, and send validated findings to the HuntOps Findings Inbox.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void campaignsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh Campaigns</button>
              {activeCampaignId && <button onClick={() => void campaignStatusQuery.refetch()} disabled={campaignStatusQuery.isFetching} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Poll Status</button>}
              {activeCampaignId && <button onClick={() => void campaignProgressQuery.refetch()} disabled={campaignProgressQuery.isFetching} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Poll Progress</button>}
              {activeCampaignId && <button onClick={() => void campaignReportQuery.refetch()} disabled={campaignReportQuery.isFetching} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Get Report</button>}
            </div>
          </div>

          {!activeCampaignId ? (
            <div className="mt-4 rounded-lg border border-dashed border-border p-8 text-center text-xs text-muted-foreground">No campaign selected yet. Submit a broad hunt prompt or open a campaign from history.</div>
          ) : (
            <div className="mt-4 grid gap-4 xl:grid-cols-[320px_1fr]">
              <div className="space-y-3">
                <div className="rounded-lg border border-border bg-muted/10 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Selected campaign</div>
                  <div className="mt-1 font-mono text-sm font-bold text-foreground">{activeCampaignId}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(String(campaignStatus?.status || 'unknown'))}`}>{prettyStatus(String(campaignStatus?.status || 'unknown'))}</span>
                    {campaignStatus?.currentPhase && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{campaignStatus.currentPhase}</span>}
                  </div>
                </div>
                <div className="grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-1">
                  <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Findings</div><div className="mt-1 font-bold text-foreground">{campaignStatus?.findingsCount ?? '—'}</div></div>
                  <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Hypotheses</div><div className="mt-1 font-bold text-foreground">{campaignStatus?.hypothesesCount ?? '—'}</div></div>
                  <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">KQL queries</div><div className="mt-1 font-bold text-foreground">{campaignStatus?.totalKqlQueries ?? '—'}</div></div>
                  <div className="rounded-lg border border-border bg-muted/10 p-3"><div className="text-[10px] uppercase text-muted-foreground">Last polled</div><div className="mt-1 font-bold text-foreground">{formatDate(campaignStatus?.lastPolledAt)}</div></div>
                </div>
                <div className="rounded-lg border border-border bg-muted/10 p-3 text-xs">
                  <div className="text-[10px] uppercase text-muted-foreground">Focus areas</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {listFromUnknown(campaignStatus?.focusAreas).length ? listFromUnknown(campaignStatus?.focusAreas).map((x) => <span key={x} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{x}</span>) : <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canExportCampaigns && <button onClick={() => void exportCampaign('json')} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Export JSON</button>}
                  {canExportCampaigns && <button onClick={() => void exportCampaign('markdown')} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Export Markdown</button>}
                  {canPromoteFindings && <button onClick={() => promoteMutation.mutate(activeCampaignId)} disabled={promoteMutation.isPending} className="rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50">{promoteMutation.isPending ? 'Sending…' : 'Send to Findings Inbox'}</button>}
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-lg border border-border bg-muted/10 p-3">
                  <div className="flex items-center justify-between gap-2"><h5 className="text-xs font-bold text-foreground">Progress timeline</h5><span className="text-[10px] text-muted-foreground">{campaignEvents.length} events</span></div>
                  <div className="mt-3 max-h-64 overflow-auto space-y-2">
                    {campaignEvents.length ? campaignEvents.map((event, idx) => (
                      <div key={idx} className="rounded border border-border bg-background p-2 text-[11px]">
                        <div className="font-semibold text-foreground">{String(event.event || event.message || event.phase || event.status || `Event ${idx + 1}`)}</div>
                        <div className="mt-1 text-muted-foreground">{String(event.t || event.time || event.timestamp || '')}</div>
                        {Object.keys(event).length > 2 && <pre className="mt-2 max-h-24 overflow-auto whitespace-pre-wrap font-mono text-[10px] text-muted-foreground">{JSON.stringify(event, null, 2)}</pre>}
                      </div>
                    )) : <div className="rounded border border-dashed border-border p-6 text-center text-xs text-muted-foreground">No progress events yet. Poll progress while the campaign is running.</div>}
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-background p-3">
                  <div className="flex items-center justify-between gap-2"><h5 className="text-xs font-bold text-foreground">Campaign report</h5>{campaignReportQuery.isFetching && <span className="text-[10px] text-muted-foreground">Loading…</span>}</div>
                  {campaignMarkdown ? (
                    <div className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded border border-border bg-muted/10 p-3 text-xs text-foreground">{campaignMarkdown}</div>
                  ) : campaignReport?.report ? (
                    <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded border border-border bg-muted/10 p-3 font-mono text-[11px] text-foreground">{JSON.stringify(campaignReport.report, null, 2)}</pre>
                  ) : campaignReportQuery.error instanceof Error ? (
                    <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">{campaignReportQuery.error.message}</div>
                  ) : (
                    <div className="mt-3 rounded border border-dashed border-border p-6 text-center text-xs text-muted-foreground">Report is not available yet. Poll status/progress, then Get Report after the campaign completes.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {campaigns.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <div className="mb-2 text-xs font-bold text-foreground">Campaign history</div>
              <table className="min-w-full text-left text-xs">
                <thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Updated</th><th className="px-2 py-2">Campaign</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Phase</th><th className="px-2 py-2">Findings</th><th className="px-2 py-2">Action</th></tr></thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.externalCampaignId} className="border-t border-border align-top">
                      <td className="px-2 py-2 text-muted-foreground">{formatDate(campaign.updatedAt)}</td>
                      <td className="px-2 py-2 font-mono text-foreground">{campaign.externalCampaignId}</td>
                      <td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(String(campaign.status || 'unknown'))}`}>{prettyStatus(String(campaign.status || 'unknown'))}</span></td>
                      <td className="px-2 py-2 text-muted-foreground">{campaign.currentPhase || '—'}</td>
                      <td className="px-2 py-2 text-muted-foreground">{campaign.findingsCount ?? '—'}{campaign.promotedFindingsCount ? ` / promoted ${campaign.promotedFindingsCount}` : ''}</td>
                      <td className="px-2 py-2"><div className="flex flex-wrap gap-1.5"><button onClick={() => setSelectedCampaignId(campaign.externalCampaignId)} className="rounded border border-border px-2 py-1 hover:bg-muted">Open</button>{canDeleteHistory && <button onClick={() => deleteCampaignMutation.mutate(campaign.externalCampaignId)} disabled={deleteCampaignMutation.isPending} className="rounded border border-red-500/30 px-2 py-1 text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {canViewIntel && (
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-500">Intel / KEV Hunts</div>
              <h4 className="mt-1 text-sm font-bold text-foreground">Threat Intel, CISA KEV and Targeted Report Hunts</h4>
              <p className="mt-1 text-xs text-muted-foreground">Launch Intel Scan, CISA KEV exposure scan, or targeted URL/text hunts through the Hunt Agent service. Results are cached and audited.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void intelJobsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh Jobs</button>
              <button onClick={() => void intelReportsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Intel Reports</button>
              <button onClick={() => void kevReportsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">KEV Reports</button>
            </div>
          </div>
          <div className="mt-4 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-lg border border-border bg-muted/10 p-3">
              <h5 className="text-xs font-bold text-foreground">Start Intel / KEV Hunt</h5>
              <div className="mt-3 space-y-2">
                <input value={intelTitle} onChange={(e) => setIntelTitle(e.target.value)} placeholder="Optional title" className="w-full rounded border border-border bg-background px-3 py-2 text-xs" />
                <input value={intelUrl} onChange={(e) => setIntelUrl(e.target.value)} placeholder="Targeted intel URL, blog, advisory or report link" className="w-full rounded border border-border bg-background px-3 py-2 text-xs" />
                <textarea value={intelText} onChange={(e) => setIntelText(e.target.value)} rows={5} placeholder="Optional pasted report/advisory text for targeted intel hunt" className="w-full rounded border border-border bg-background px-3 py-2 text-xs" />
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <button onClick={() => submitIntelMutation.mutate('intel_scan')} disabled={!canRunIntel || submitIntelMutation.isPending || !workspaceReady} className="rounded bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50">Intel Scan</button>
                  <button onClick={() => submitIntelMutation.mutate('kev_scan')} disabled={!canRunIntel || submitIntelMutation.isPending || !workspaceReady} className="rounded bg-purple-600 px-3 py-2 text-xs font-bold text-white hover:bg-purple-700 disabled:opacity-50">CISA KEV Scan</button>
                  <button onClick={() => submitIntelMutation.mutate('targeted_intel_hunt')} disabled={!canRunIntel || submitIntelMutation.isPending || !workspaceReady || (!intelUrl.trim() && !intelText.trim())} className="rounded bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">Targeted Hunt</button>
                </div>
                {!canRunIntel && <div className="text-[11px] text-muted-foreground">You have view access only. Request hunts.run_threat_hunt_intel to launch scans.</div>}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="flex items-center justify-between gap-2"><h5 className="text-xs font-bold text-foreground">Latest Intel / KEV Result</h5>{latestIntelJob && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(latestIntelJob.status)}`}>{prettyStatus(latestIntelJob.status)}</span>}</div>
              {latestIntelJob ? (
                <div className="mt-3 space-y-2 text-xs">
                  <div className="grid gap-2 md:grid-cols-3">
                    <div className="rounded border border-border bg-muted/10 p-2"><div className="text-[10px] uppercase text-muted-foreground">Type</div><div className="font-semibold text-foreground">{prettyStatus(latestIntelJob.jobType)}</div></div>
                    <div className="rounded border border-border bg-muted/10 p-2"><div className="text-[10px] uppercase text-muted-foreground">External ID</div><div className="font-mono text-foreground">{latestIntelJob.externalId || '—'}</div></div>
                    <div className="rounded border border-border bg-muted/10 p-2"><div className="text-[10px] uppercase text-muted-foreground">Updated</div><div className="text-foreground">{formatDate(latestIntelJob.updatedAt)}</div></div>
                  </div>
                  <div className="rounded border border-border bg-muted/10 p-2"><div className="text-[10px] uppercase text-muted-foreground">Title / input</div><div className="text-foreground">{latestIntelJob.title || latestIntelJob.sourceUrl || latestIntelJob.inputText || '—'}</div></div>
                  {latestIntelJob.errorMessage && <div className="rounded border border-red-500/30 bg-red-500/10 p-2 text-red-700 dark:text-red-300">{latestIntelJob.errorMessage}</div>}
                  <pre className="max-h-72 overflow-auto rounded border border-border bg-muted/10 p-3 font-mono text-[11px] text-foreground">{JSON.stringify(latestIntelJob.rawResult || latestIntelJob.rawSubmit || latestIntelJob, null, 2)}</pre>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => refreshIntelMutation.mutate(latestIntelJob.id)} disabled={refreshIntelMutation.isPending} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">Poll Job</button>
                    {canExportIntel && <button onClick={() => void exportIntelJob(latestIntelJob.id, 'json')} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Export JSON</button>}
                    {canExportIntel && <button onClick={() => void exportIntelJob(latestIntelJob.id, 'markdown')} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Export Markdown</button>}
                    {canDeleteHistory && <button onClick={() => deleteIntelMutation.mutate(latestIntelJob.id)} disabled={deleteIntelMutation.isPending} className="rounded border border-red-500/30 px-3 py-1.5 text-xs text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}
                  </div>
                </div>
              ) : <div className="mt-3 rounded border border-dashed border-border p-6 text-center text-xs text-muted-foreground">No Intel / KEV hunt jobs recorded yet.</div>}
            </div>
          </div>
          {canViewScheduledIntel && (
            <div className="mt-4 rounded-lg border border-border bg-muted/10 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><h5 className="text-xs font-bold text-foreground">Scheduled KEV / Intel Monitoring</h5><p className="mt-1 text-[11px] text-muted-foreground">Create recurring KEV, Intel Scan or targeted report monitoring for this customer. Due schedules can be triggered manually now or later by cron/Logic App against the run-due endpoint.</p></div>
                <div className="flex flex-wrap gap-2"><button onClick={() => void intelSchedulesQuery.refetch()} className="rounded border border-border px-2 py-1 text-xs hover:bg-muted">Refresh</button>{canRunScheduledIntel && <button onClick={() => runDueIntelSchedulesMutation.mutate()} disabled={runDueIntelSchedulesMutation.isPending} className="rounded border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50">Run Due</button>}</div>
              </div>
              <div className="mt-3 grid gap-2 lg:grid-cols-[220px_160px_1fr_auto]">
                <select value={intelScheduleJobType} onChange={(e) => setIntelScheduleJobType(e.target.value as typeof intelScheduleJobType)} className="rounded border border-border bg-background px-3 py-2 text-xs">
                  <option value="kev_scan">CISA KEV scan</option>
                  <option value="intel_scan">Intel scan</option>
                  <option value="targeted_intel_hunt">Targeted intel hunt</option>
                </select>
                <select value={intelScheduleCadence} onChange={(e) => setIntelScheduleCadence(e.target.value)} className="rounded border border-border bg-background px-3 py-2 text-xs"><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="weekly">Weekly</option></select>
                <div className="rounded border border-border bg-background px-3 py-2 text-[11px] text-muted-foreground">Targeted schedules reuse the URL/text fields above. KEV/Intel schedules can run without URL/text.</div>
                <button onClick={() => createIntelScheduleMutation.mutate()} disabled={!canManageScheduledIntel || createIntelScheduleMutation.isPending || (intelScheduleJobType === 'targeted_intel_hunt' && !intelUrl.trim() && !intelText.trim())} className="rounded bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50">Create Schedule</button>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {intelSchedules.length ? intelSchedules.map((schedule) => (<div key={schedule.id} className="rounded border border-border bg-background p-3 text-xs"><div className="flex items-center justify-between gap-2"><div className="font-bold text-foreground">{schedule.title || prettyStatus(schedule.jobType)}</div><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${schedule.enabled ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border text-muted-foreground'}`}>{schedule.enabled ? 'Enabled' : 'Disabled'}</span></div><div className="mt-1 text-muted-foreground">{prettyStatus(schedule.jobType)} · {schedule.cadence} · next {formatDate(schedule.nextRunAt)}</div>{schedule.lastJobId && <div className="mt-1 font-mono text-[10px] text-muted-foreground">last job {schedule.lastJobId}</div>}<div className="mt-2 flex flex-wrap gap-1.5">{canRunScheduledIntel && <button onClick={() => runIntelScheduleMutation.mutate(schedule.id)} disabled={runIntelScheduleMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Run</button>}{canManageScheduledIntel && <button onClick={() => toggleIntelScheduleMutation.mutate(schedule)} disabled={toggleIntelScheduleMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">{schedule.enabled ? 'Disable' : 'Enable'}</button>}{canManageScheduledIntel && <button onClick={() => deleteIntelScheduleMutation.mutate(schedule.id)} disabled={deleteIntelScheduleMutation.isPending} className="rounded border border-red-500/30 px-2 py-1 text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}</div></div>)) : <div className="rounded border border-dashed border-border p-5 text-center text-xs text-muted-foreground">No Intel / KEV monitoring schedules yet.</div>}
              </div>
            </div>
          )}

          {intelJobs.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <div className="mb-2 text-xs font-bold text-foreground">Intel / KEV job history</div>
              <table className="min-w-full text-left text-xs">
                <thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Updated</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Title</th><th className="px-2 py-2">External</th><th className="px-2 py-2">Action</th></tr></thead>
                <tbody>{intelJobs.map((job) => (<tr key={job.id} className="border-t border-border align-top"><td className="px-2 py-2 text-muted-foreground">{formatDate(job.updatedAt)}</td><td className="px-2 py-2 text-muted-foreground">{prettyStatus(job.jobType)}</td><td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(job.status)}`}>{prettyStatus(job.status)}</span></td><td className="px-2 py-2"><div className="max-w-md truncate font-semibold text-foreground">{job.title || job.sourceUrl || job.inputText || '—'}</div>{job.errorMessage && <div className="mt-1 max-w-md truncate text-[10px] text-amber-600 dark:text-amber-300">{job.errorMessage}</div>}</td><td className="px-2 py-2 font-mono text-muted-foreground">{job.externalId || '—'}</td><td className="px-2 py-2"><div className="flex flex-wrap gap-1.5"><button onClick={() => setSelectedIntelJob(job)} className="rounded border border-border px-2 py-1 hover:bg-muted">Open</button><button onClick={() => refreshIntelMutation.mutate(job.id)} disabled={refreshIntelMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Poll</button>{canDeleteHistory && <button onClick={() => deleteIntelMutation.mutate(job.id)} disabled={deleteIntelMutation.isPending} className="rounded border border-red-500/30 px-2 py-1 text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}</div></td></tr>))}</tbody>
              </table>
            </div>
          )}
          <details className="mt-4 rounded-lg border border-border bg-muted/10 p-3 text-xs">
            <summary className="cursor-pointer font-bold text-foreground">External Intel / KEV reports cache</summary>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <pre className="max-h-80 overflow-auto rounded border border-border bg-background p-3 font-mono text-[11px] text-foreground">{JSON.stringify(intelReportsQuery.data?.reports || intelReportsQuery.error || { message: 'No intel reports loaded yet.' }, null, 2)}</pre>
              <pre className="max-h-80 overflow-auto rounded border border-border bg-background p-3 font-mono text-[11px] text-foreground">{JSON.stringify(kevReportsQuery.data?.reports || kevReportsQuery.error || { message: 'No KEV reports loaded yet.' }, null, 2)}</pre>
            </div>
          </details>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-bold text-foreground">Agent Request History</h4>
            <p className="mt-1 text-xs text-muted-foreground">Async requests and local compatibility responses for this customer.</p>
          </div>
          <button onClick={() => void requestsQuery.refetch()} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Refresh</button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="text-[11px] uppercase tracking-wide text-muted-foreground"><tr><th className="px-2 py-2">Time</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Question</th><th className="px-2 py-2">Workspace</th><th className="px-2 py-2">Route</th><th className="px-2 py-2">User</th><th className="px-2 py-2">Action</th></tr></thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-t border-border align-top">
                  <td className="px-2 py-2 text-muted-foreground">{formatDate(req.createdAt)}</td>
                  <td className="px-2 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusBadge(req.status)}`}>{prettyStatus(req.status)}</span></td>
                  <td className="px-2 py-2"><div className="max-w-xl truncate font-semibold text-foreground">{req.message}</div>{req.errorMessage && <div className="mt-1 max-w-xl truncate text-[10px] text-amber-600 dark:text-amber-300">{req.errorMessage}</div>}</td>
                  <td className="px-2 py-2 text-muted-foreground"><div>{req.workspaceName ?? '—'}</div><div className="font-mono text-[10px]">{maskGuid(req.workspaceId)}</div></td>
                  <td className="px-2 py-2 text-muted-foreground">{req.route ?? '—'}</td>
                  <td className="px-2 py-2 text-muted-foreground">{req.createdByEmail ?? '—'}</td>
                  <td className="px-2 py-2"><div className="flex flex-wrap gap-1.5"><button onClick={() => { setSelected(req); if (req.externalCampaignId) setSelectedCampaignId(req.externalCampaignId); }} className="rounded border border-border px-2 py-1 hover:bg-muted">Open</button><button onClick={() => refreshMutation.mutate(req.id)} disabled={refreshMutation.isPending} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50">Poll</button>{canDeleteHistory && <button onClick={() => deleteRequestMutation.mutate(req.id)} disabled={deleteRequestMutation.isPending} className="rounded border border-red-500/30 px-2 py-1 text-red-600 hover:bg-red-500/10 disabled:opacity-50">Delete</button>}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {requests.length === 0 && !requestsQuery.isLoading && <div className="py-8 text-center text-xs italic text-muted-foreground">No threat hunt agent requests recorded yet.</div>}
        </div>
      </div>

      {!status?.configured && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs text-amber-800 dark:text-amber-200">
          <div className="font-bold">MSSP Hunt Agent service not configured</div>
          <p className="mt-1">For the KloudSecure VM-sidecar deployment, point the API service at the local listener. External mode remains available only for rollback or a deliberately remote deployment.</p>
          <pre className="mt-3 overflow-auto rounded border border-amber-500/30 bg-background p-3 font-mono text-[11px] text-foreground">{`MSSP_HUNT_AGENT_ENABLED=true
MSSP_HUNT_AGENT_MODE=local
MSSP_HUNT_AGENT_URL=http://127.0.0.1:7108
MSSP_HUNT_AGENT_API_PREFIX=/api/v1
MSSP_HUNT_AGENT_MODEL=gpt-4.1-mini
MSSP_HUNT_AGENT_TIMEOUT_MS=45000`}</pre>
        </div>
      )}
    </div>
  );
}
